// src/JudgeTarget.tsx
// ─────────────────────────────────────────────────────────────────────────────
//  Obrazovka rozhodčího – Terče (papírové)
//
//  Terčový rozhodčí počítá terče v místnosti, ručně vybírá:
//    - Rundu
//    - Stanoviště (pokud je přiřazen na více)
//  A zadává celkovou sumu bodů pro každého střelce rundy.
//  Systém validuje, zda je suma dosažitelná.
//
//  Zápis výsledku (fáze 1 živého bodování, docs/live-faze1-navrh.md):
//    - mění jen JEDNO stanoviště střelce (saveStationResult – mergeFields);
//      ostatní stanoviště a opravy hlavního rozhodčího zůstanou netknuté,
//    - body se ukládají zaokrouhlené na desetiny, jak je ověřuje kontrola terče,
//    - neupravený řádek se obnoví při změně i smazání výsledku jinde,
//      upravený zůstane, jak ho rozhodčí napsal,
//    - „Uložit vše“ uloží jen upravené řádky lišící se od uložené hodnoty
//      a všechny zápisy spustí najednou (offline se zařadí všechny); hodnotu,
//      kterou server právě zamítl, znovu neposílá (jen tlačítko u řádku)
//      a když nic jiného k uložení není, řekne to (ne „Žádné změny“),
//    - každý zápis se poznamená do schránky „k odeslání“ (utils/liveOutbox);
//      zamítnutí se ukáže u řádku, záznam ve schránce zůstane a rodič ho
//      porovná se serverem (upozornění i po ukončení závodu nebo odchodu
//      z obrazovky, kdy hláška u řádku zmizí); zamítnutí se ohlásí
//      i posluchačům schránky (obrazovka rozhodčího otevřená znovu).
//
//  Fáze 3 (3a): stanoviště přichází živě – terč a počet ran se pro kontrolu
//  bodů zmrazí, dokud je některý řádek rozepsaný nebo se ukládá (změna
//  nastavení za běhu platí od dalšího zadání); terč se čte jedním dokumentem.
//
//  Fáze 3 (3b, rozhodnutí majitele 10): hlášky z ToastProvideru (pod lištou
//  rozhodčího, H1); „Zadat po ranách“ jen otevírá a zavírá výběr zón – uloží
//  se tlačítkem „OK – Uložit“ nebo u řádku (H2); řádek s uloženou hodnotou
//  ukazuje trvale „Uloženo“ (H3); „Uložit vše“ končí jednou souhrnnou
//  hláškou (H4).
//
//  Fáze 4b (rozhodnutí majitele 8. 10. 2026 – počet zásahů při shodě bodů,
//  utils/tieRules.ts): terč se zadává JEN po ranách. Pole bodů ukazuje
//  součet ran (jen pro čtení – klepnutí otevře výběr zón), zóna 0 (minul)
//  je ve výběru vždy. Uloží se body i hodnota každé rány (`shots`, od
//  nejvyšší); uložit jde až se všemi ranami. Uložený výsledek se do výběru
//  předvyplní z ran; starší výsledek jen se součtem ukáže „bez rozpisu ran“
//  a jde zadat znovu po ranách. Beze změny (stejné body i rány) se nic
//  nezapisuje. Terč bez zón (nebo nenačtený) se zadává součtem jako dřív –
//  počet zásahů pak o shodě nerozhodne.
//
//  Krok 4 – automatická rotace (docs/live-auto-rotace-navrh.md R3, rozbor
//  §5.7): výběr rundy zůstává ruční; bez vybrané rundy se předvybere runda,
//  která podle dat stojí na terči (u tlačítka „teď“). Rozhodčí terče po
//  odstřílení rundy ťukne „Runda N odstřílela – poslat dál“ (uvolnění bez
//  seznamu chybějících) – bez toho runda odejde, až budou zapsané všechny
//  výsledky terče. Výsledky jde zadávat i potom.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import {
  LiveStation,
  LiveRound,
  RoundShooter,
  LiveResult,
  Target,
  isValidTargetScore,
} from "./types/liveScoring";
import { useToast } from "./ui/Toast";
import { roundPoints } from "./utils/formatPoints";
import { liveSaveErrorMessage, saveStationResult } from "./utils/liveResults";
import { notifyOutboxRejected, outboxAdd, outboxList, outboxRemove, type OutboxEntry } from "./utils/liveOutbox";
import { reportError } from "./utils/reportError";

interface Props {
  raceId: string;
  station: LiveStation;
  rounds: LiveRound[];
  liveResults: { [startNum: number]: LiveResult };
  judgeUid: string;
  /**
   * Server zápis řádku zamítl (volá se i po odchodu z obrazovky) – rodič
   * porovná schránku „k odeslání“ se serverem a ukáže upozornění.
   */
  onSaveRejected?: () => void;
  /** Server zápis řádku potvrdil – znovu uložený výsledek zmizí z upozornění. */
  onSaveSucceeded?: () => void;
  /** Automatická rotace (krok 4); null / bez = ruční rundy. */
  autoRotation?: TargetAutoRotation | null;
}

/** Automatická rotace na terči: navržená runda a „odstřílela – poslat dál“. */
export interface TargetAutoRotation {
  /** Runda, která podle dat stojí na terči (null = žádná). */
  suggested: number | null;
  /** Runda už terč opustila (uvolněná nebo hotová) nebo není v rotaci – tlačítko se nenabízí. */
  isSentOn: (roundNumber: number) => boolean;
  /** Rozhodčí smí uvolnit (rozhodčí závodu, hlavní rozhodčí, správce). */
  canRelease: boolean;
  /** Uvolnění s potvrzením (zápis dělá obrazovka rozhodčího). */
  onRelease: (roundNumber: number) => void;
}

/** Body do pole – desetinná čárka jako dosud (90.1 → "90,1"), bez float šumu. */
const toInput = (score: number) => String(Math.round(score * 100) / 100).replace(".", ",");

/** Stejné body (uložená hodnota může mít float šum nebo starší 2 des. místa). */
const sameScore = (a: number, b: number) => Math.abs(a - b) < 0.005;

/**
 * Běh „Uložit vše“: řádky si hlášku nedávají samy – na konci jedna souhrnná
 * (H4). Uložená a neuložená (neplatná hodnota, zamítnutí) startovní čísla.
 */
interface BulkRun {
  saved: number[];
  notSaved: number[];
  /**
   * Zamítnutí řádků, které už obrazovka neukazuje (jiná runda / stanoviště):
   * hláška u řádku není – důvod jde do souhrnu.
   */
  elsewhere: string[];
}

/** 1 výsledek, 2–4 výsledky, 5 a víc výsledků. */
const resultsWord = (n: number) => (n === 1 ? "výsledek" : n >= 2 && n <= 4 ? "výsledky" : "výsledků");

/** Zóny výběru od nejvyšší, vždy i 0 (minul) – terč bez zóny 0 (presety) by ránu mimo nešlo zadat. */
const zoneList = (zones: ReadonlyArray<number>): number[] => {
  const keys = new Set<number>();
  const out: number[] = [];
  [...zones, 0].forEach(z => {
    if (typeof z !== "number" || !Number.isFinite(z)) return;
    const k = Math.round(z * 100);
    if (keys.has(k)) return;
    keys.add(k);
    out.push(k / 100);
  });
  return out.sort((a, b) => b - a);
};

/** Rány podle počtů zón – hodnota každé rány od nejvyšší. */
const shotsOfCounts = (counts: { [zone: number]: number }): number[] => {
  const out: number[] = [];
  Object.keys(counts).forEach(z => {
    const value = parseFloat(z);
    for (let i = 0; i < (counts as any)[z]; i++) out.push(value);
  });
  return out.sort((a, b) => b - a);
};

/** Počty zón z uložených ran; null = rána mimo zóny terče (nelze předvyplnit). */
const countsOfShots = (shots: unknown, zones: ReadonlyArray<number>): { [zone: number]: number } | null => {
  if (!Array.isArray(shots)) return null;
  const counts: { [zone: number]: number } = {};
  for (const shot of shots) {
    if (typeof shot !== "number") return null;
    const zone = zones.find(z => Math.abs(z - shot) < 0.005);
    if (zone === undefined) return null;
    counts[zone] = (counts[zone] || 0) + 1;
  }
  return counts;
};

/** Stejné rány (pořadí nehraje roli). */
const sameShots = (a: ReadonlyArray<number>, b: ReadonlyArray<number>): boolean => {
  if (a.length !== b.length) return false;
  const x = a.map(v => Math.round(v * 100)).sort((p, q) => p - q);
  const y = b.map(v => Math.round(v * 100)).sort((p, q) => p - q);
  return x.every((v, i) => v === y[i]);
};

/** Hodnota v poli odpovídá uloženým bodům – řádek je uložený (H3). */
const showsScore = (raw: string, stored: number | null): boolean => {
  if (stored === null || raw.trim() === "") return false;
  const parsed = parseFloat(raw.replace(",", "."));
  return Number.isFinite(parsed) && sameScore(parsed, stored);
};

const JudgeTarget: React.FC<Props> = ({
  raceId,
  station,
  rounds,
  liveResults,
  judgeUid,
  onSaveRejected,
  onSaveSucceeded,
  autoRotation = null,
}) => {
  // Terč a počet ran pro kontrolu bodů – zmrazené, dokud je některý řádek
  // rozepsaný nebo se ukládá (nastavení změněné za běhu platí od dalšího
  // zadání; bez toho by se rozepsaný řádek kontroloval jiným terčem)
  const liveTargetCfg = {
    targetId: ((station.config as any)?.targetId as string) || "",
    targetName: ((station.config as any)?.targetName as string) || "",
    shotsCount: station.shotsCount,
  };
  const frozenCfgRef = useRef<{ stationId: string; cfg: typeof liveTargetCfg } | null>(null);

  // ── State ─────────────────────────────────────────────────
  const [selectedRoundNum, setSelectedRoundNum] = useState<number | null>(null);
  const [scores, setScores]   = useState<{ [startNum: number]: string }>({});
  const [saving, setSaving]   = useState<{ [startNum: number]: boolean }>({});
  const [target, setTarget]   = useState<Target | null>(null);
  // Hlášky z provideru v kořeni aplikace – pod horní lištou rozhodčího (H1).
  // Provider žije dál i po odchodu z obrazovky: hláška o úspěchu jen na ní,
  // zamítnutí vždy (výsledek se neuložil – upozornění rodiče ho ukáže taky)
  const { showToast } = useToast();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // Neuložený výsledek (zamítnutý zápis) – hláška u řádku
  const [errors, setErrors]   = useState<{ [startNum: number]: string }>({});
  // Zone picker: { [startNum]: { [zone]: count } }
  const [expandedShooter, setExpandedShooter] = useState<number | null>(null);
  const [zoneCounts, setZoneCounts] = useState<{ [startNum: number]: { [zone: number]: number } }>({});

  // Předvyplnění: řádky, které rozhodčí upravil (psaním nebo po ranách) od
  // posledního předvyplnění / uložení – ty se změnou výsledků jinde nepřepíšou.
  const dirtyRef    = useRef<{ [startNum: number]: boolean }>({});
  // Výchozí hodnota řádku: uložené body, podle kterých je neupravený řádek
  // předvyplněný (po spuštění zápisu ukládané body); null = bez výsledku.
  const baselineRef = useRef<{ [startNum: number]: number | null }>({});
  // Výběr stanoviště a rundy – odpověď zápisu z dřívějšího výběru nesmí měnit
  // řádky nového (stejná startovní čísla na jiném stanovišti!).
  const selectionRef = useRef(0);
  const [selectionSeq, setSelectionSeq] = useState(0);
  // Poslední zápis řádku – spinner, „Uloženo“ i chyba patří jen jemu.
  const lastWriteRef = useRef<{ [startNum: number]: number }>({});
  const writeSeqRef  = useRef(0);
  // Zápisy aktuálního výběru čekající na server: předvyplnění řádek nepřepíše
  // (místní kopie výsledků může být do promítnutí zápisu ještě stará –
  // u „Uložit vše“ se zápisy promítají postupně)
  const inFlightRef  = useRef<{ [startNum: number]: number }>({});
  // Čas zadání pro schránku „k odeslání“ (rostoucí – identifikuje zápis)
  const lastAtRef    = useRef(0);
  // Body a rány posledního zamítnutého zápisu řádku: „Uložit vše“ je znovu
  // neposílá (zamítly by se zas), dokud je rozhodčí nezmění – zopakovat je
  // jde tlačítkem u řádku
  const rejectedRef  = useRef<{ [startNum: number]: string }>({});

  const rowsBusy =
    Object.keys(dirtyRef.current).some(k => dirtyRef.current[Number(k)]) ||
    Object.keys(inFlightRef.current).length > 0;
  if (!frozenCfgRef.current || frozenCfgRef.current.stationId !== station.id || !rowsBusy) {
    frozenCfgRef.current = { stationId: station.id, cfg: liveTargetCfg };
  }
  const { targetId, targetName, shotsCount } = frozenCfgRef.current.cfg;

  // Načti terč z databáze pro validaci (jen jeden dokument)
  useEffect(() => {
    setTarget(null);
    if (!targetId) return;
    let cancelled = false;
    getDoc(doc(db, "targets", targetId))
      .then(snap => {
        if (!cancelled && snap.exists()) setTarget({ id: snap.id, ...(snap.data() as any) });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [targetId]);

  const selectedRound = rounds.find(r => r.number === selectedRoundNum) || null;
  // Fáze 4b: terč se zónami se zadává jen po ranách (zóna 0 = minul vždy)
  const zones = target && target.zones.length > 0 ? zoneList(target.zones) : [];
  const perShot = zones.length > 0 && shotsCount > 0;
  // Řádky tohoto stanoviště se zápisem ve schránce „k odeslání“ (nepotvrzené serverem)
  const queuedRows = new Set(
    outboxList(raceId, judgeUid).filter(e => e.stationId === station.id).map(e => e.startNumber)
  );
  const activeShooters = selectedRound?.shooters.filter(s => s.active) || [];

  /** Uložené body střelce na tomto stanovišti (místní kopie); null = bez výsledku. */
  const storedScore = (startNum: number): number | null => {
    const entry = liveResults[startNum]?.stations?.[station.id];
    return entry && typeof entry.score === "number" ? entry.score : null;
  };
  /**
   * Uložené rány (hodnoty zón) – jen když odpovídají zónám terče a jejich
   * součet dá uložené body (oprava hlavního rozhodčího jen bodů nechá staré
   * rány – pak „bez rozpisu ran“ jako na tabuli a v Živých výsledcích);
   * jinak null. Počet ran se nekontroluje (změna nastavení za běhu – tabule
   * rozpis bere dál; nové uložení chce aktuální počet).
   */
  const storedShots = (startNum: number): number[] | null => {
    const entry = liveResults[startNum]?.stations?.[station.id];
    const shots = entry ? (entry as { shots?: unknown }).shots : undefined;
    if (!perShot || !entry || typeof entry.score !== "number" || !Array.isArray(shots) || shots.length === 0) return null;
    if (!countsOfShots(shots, zones)) return null;
    const sum = (shots as number[]).reduce((acc, v) => acc + v, 0);
    return Math.abs(sum - entry.score) < 0.005 ? (shots as number[]) : null;
  };
  /** Uložený výsledek je stejný: body (a u zadání po ranách i rány). */
  const storedSame = (startNum: number, score: number, shots: number[]): boolean => {
    const stored = storedScore(startNum);
    if (stored === null || !sameScore(stored, score)) return false;
    if (!perShot) return true;
    const st = storedShots(startNum);
    return !!st && sameShots(st, shots);
  };
  /** Podpis zápisu (zamítnutá hodnota se „Uložit vše“ znovu neposílá). */
  const writeSig = (score: number, shots: number[]) => `${Math.round(score * 100)}|${shots.join(",")}`;

  // ── Validace skóre ────────────────────────────────────────
  // Kontrola terče počítá na desetiny (zóny jako 30,1) – body se proto ukládají
  // zaokrouhlené na desetiny, přesně jak je ověřila (překlep 90,14 → 90,1).
  // Bez načteného terče (nebo terče bez zón) na 2 des. místa bez float šumu.
  const validateScore = (rawValue: string): { valid: boolean; score: number; reason?: string } => {
    const raw = rawValue.replace(",", ".");
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) return { valid: false, score: 0, reason: "Není číslo" };
    const checksTenths = !!target && target.zones.length > 0 && shotsCount > 0;
    const score = roundPoints(parsed, checksTenths ? 1 : 2) || 0; // || 0: bez -0
    if (!target) return { valid: true, score }; // bez terče nelze validovat
    const ok = isValidTargetScore(target.zones, shotsCount, score);
    if (!ok) return {
      valid: false,
      score,
      reason: `${parsed} bodů není platná kombinace pro ${shotsCount} ran (zóny: ${target.zones.join(", ")})`
    };
    return { valid: true, score };
  };

  /**
   * Kontrola řádku: po ranách (4b) musí být zadané všechny rány – body jsou
   * jejich součet; terč bez zón součtem jako dřív (validateScore).
   */
  const validateRow = (startNum: number, rawValue: string): { valid: boolean; score: number; shots: number[]; reason?: string } => {
    if (!perShot) {
      const v = validateScore(rawValue);
      return { ...v, shots: [] };
    }
    const shots = shotsOfCounts(zoneCounts[startNum] || {});
    if (shots.length !== shotsCount) {
      return { valid: false, score: 0, shots, reason: `Zadejte všech ${shotsCount} ran po ranách (zadáno ${shots.length}).` };
    }
    // Součet zón je vždy dosažitelný (kontrola terče by navíc počítala jen na desetiny)
    const score = roundPoints(shots.reduce((sum, v) => sum + v, 0), 2) || 0;
    return { valid: true, score, shots };
  };

  const clearError = (startNum: number) =>
    setErrors(prev => {
      if (prev[startNum] === undefined) return prev;
      const next = { ...prev };
      delete next[startNum];
      return next;
    });

  /** Řádek odpovídá uložené hodnotě – zase se předvyplňuje (obnoví se při změně jinde). */
  const markClean = (startNum: number, stored: number) => {
    dirtyRef.current[startNum] = false;
    baselineRef.current[startNum] = stored;
    delete rejectedRef.current[startNum];
    setScores(prev => ({ ...prev, [startNum]: toInput(stored) }));
    clearError(startNum);
  };

  // ── Uložení jednoho střelce ───────────────────────────────
  // Zápis se zařadí do fronty Firestore hned při volání (i offline) – „Uložit
  // vše“ tak spustí všechny zápisy dřív, než čeká na první. Vrácený slib se
  // vyřeší po odpovědi serveru a nikdy nezamítne (chyba se ukáže u řádku),
  // takže na všechny jde čekat jako Promise.allSettled.
  const saveRow = async (shooter: RoundShooter, bulk?: BulkRun): Promise<void> => {
    const sn = shooter.startNumber;
    const { valid, score, shots, reason } = validateRow(sn, scores[sn] ?? "");

    if (!valid) {
      // Důvod je vidět i u řádku; hláška zmizí sama (oprava je na rozhodčím)
      if (bulk) bulk.notSaved.push(sn);
      else showToast(reason || "Neplatná hodnota.", "warning");
      return;
    }
    if (selectedRoundNum === null) return;

    const stored = storedScore(sn);
    if (stored !== null && storedSame(sn, score, shots)) {
      // Beze změny – nic nezapisovat: zápis by zrušil rozstřel a u stanoviště
      // opraveného hlavním rozhodčím by se zbytečně zamítl
      markClean(sn, stored);
      // Probíhající zápis řádku ohlásí „Uloženo“ až po potvrzení; řádek pak
      // ukazuje „Uloženo“ trvale (H3)
      if (!saving[sn] && !bulk) showToast(`Uloženo: #${sn} ${shooter.name} – ${toInput(stored)} b.`, "success");
      return;
    }

    const selection = selectionRef.current;
    const seq = ++writeSeqRef.current;
    const at = Math.max(Date.now(), lastAtRef.current + 1);
    lastAtRef.current = at;
    // Rány do schránky (4b) – ztracený zápis pak jde „Odeslat znovu“ jako u gongů
    const entry: OutboxEntry = { raceId, startNumber: sn, stationId: station.id, score, uid: judgeUid, at, ...(shots.length > 0 ? { shots } : {}) };
    // Odpověď patří k aktuálnímu výběru a je to poslední zápis řádku
    const isCurrent = () => selectionRef.current === selection && lastWriteRef.current[sn] === seq;

    // Od spuštění zápisu je řádek neupravený s výchozí hodnotou = ukládané body
    // (místní zápis se hned promítne do výsledků a předvyplnění ho potvrdí)
    dirtyRef.current[sn] = false;
    baselineRef.current[sn] = score;
    lastWriteRef.current[sn] = seq;
    inFlightRef.current[sn] = seq;
    delete rejectedRef.current[sn];
    setScores(prev => ({ ...prev, [sn]: toInput(score) }));
    setSaving(prev => ({ ...prev, [sn]: true }));
    clearError(sn);

    outboxAdd(entry);
    try {
      await saveStationResult(
        raceId,
        {
          startNumber:    sn,
          userId:         shooter.userId,
          sequenceNumber: shooter.sequenceNumber,
          name:           shooter.name,
          roundNumber:    selectedRoundNum,
        },
        station.id,
        // Fáze 4b: i hodnota každé rány (počet zásahů při shodě bodů);
        // terč bez zón jen součet
        { score, shots, enteredBy: judgeUid },
        liveResults[sn],
        { deleteDraft: false } // terče koncept střel nemají
      );
      outboxRemove(entry);
      onSaveSucceeded?.();
      // „Uloženo“ u řádku se odvozuje z uložené hodnoty (H3)
      if (bulk) bulk.saved.push(sn);
      else if (mountedRef.current) showToast(`Uloženo: #${sn} ${shooter.name} – ${toInput(score)} b.`, "success");
    } catch (err) {
      // Záznam ve schránce zůstane: hláška u řádku zmizí s obrazovkou (závod
      // mezitím ukončen, rozhodčí odešel), upozornění rodiče ne
      reportError("JudgeTarget.save", err, { raceId, startNumber: sn, stationId: station.id });
      const message = liveSaveErrorMessage(err);
      const current = isCurrent();
      if (current) {
        setErrors(prev => ({ ...prev, [sn]: message }));
        rejectedRef.current[sn] = writeSig(score, shots);
        if (!dirtyRef.current[sn] && baselineRef.current[sn] === score) {
          // Zadaná hodnota zůstane v poli jako upravená – vrácení místní kopie
          // ji nepřepíše a rozhodčí ji může nahlásit nebo uložit znovu
          dirtyRef.current[sn] = true;
          setScores(prev => ({ ...prev, [sn]: toInput(score) }));
        }
      }
      if (bulk) {
        bulk.notSaved.push(sn);
        if (!current) bulk.elsewhere.push(`#${sn} ${shooter.name}: ${message}`);
      } else showToast(`#${sn} ${shooter.name}: ${message}`, "error");
      onSaveRejected?.();
      // Rodič mezitím mohl zmizet (rozhodčí odešel a obrazovku otevřel znovu) –
      // zamítnutí se dozví i aktuální obrazovka rozhodčího
      notifyOutboxRejected();
    } finally {
      if (inFlightRef.current[sn] === seq) delete inFlightRef.current[sn];
      if (lastWriteRef.current[sn] === seq) setSaving(prev => ({ ...prev, [sn]: false }));
    }
  };

  const handleSave = (shooter: RoundShooter) => saveRow(shooter);

  // ── Uložit celou rundu najednou ───────────────────────────
  // Jen řádky, které rozhodčí upravil a liší se od uložené hodnoty – předvyplněný
  // řádek by jinak mohl vrátit opravu hlavního rozhodčího. Každý střelec zvlášť
  // (ne jedna dávka celé rundy – zamítnutí jednoho neshodí ostatní) a všechny
  // zápisy se spustí najednou: offline se zařadí všechny, ne jen první.
  // Hodnotu, kterou server zamítl (např. stanoviště opravené hlavním
  // rozhodčím), znovu neposílá – zamítla by se zas a s ní i hláška „Uloženo“
  // ostatních řádků; zopakovat ji jde tlačítkem u řádku. Když kromě ní není
  // co uložit, hláška to řekne – „Žádné změny“ by odporovala hodnotě a chybě
  // u řádku. Po odpovědi serveru na všechny zápisy jedna souhrnná hláška (H4:
  // „Uloženo N, neuloženo M“ – vynechané zamítnuté hodnoty mezi neuloženými);
  // bez signálu přijde, až se zápisy odešlou (řádky do té doby točí kolečko).
  const handleSaveAll = async () => {
    const bulk: BulkRun = { saved: [], notSaved: [], elsewhere: [] };
    const pending: Promise<void>[] = [];
    // Řádky vynechané kvůli zamítnuté hodnotě
    const skippedRejected: number[] = [];
    for (const shooter of activeShooters) {
      const sn = shooter.startNumber;
      const raw = scores[sn] ?? "";
      if (!dirtyRef.current[sn] || (raw.trim() === "" && !perShot)) continue;
      const { valid, score, shots } = validateRow(sn, raw);
      if (perShot && shots.length === 0) continue; // po ranách nic nezadáno
      const stored = storedScore(sn);
      if (valid && stored !== null && storedSame(sn, score, shots)) {
        markClean(sn, stored); // upravený, ale stejný jako uložený
        continue;
      }
      const rejected = rejectedRef.current[sn];
      if (rejected !== undefined && valid && rejected === writeSig(score, shots)) {
        skippedRejected.push(sn);
        continue;
      }
      pending.push(saveRow(shooter, bulk));
    }
    if (pending.length === 0) {
      if (skippedRejected.length > 0) {
        const which = skippedRejected.map(sn => `#${sn}`).join(", ");
        showToast(
          skippedRejected.length === 1
            ? `${which}: zamítnutý výsledek se znovu neposílá – uložte ho tlačítkem u řádku, nebo ho nahlaste hlavnímu rozhodčímu.`
            : `${which}: zamítnuté výsledky se znovu neposílají – uložte je tlačítkem u řádku, nebo je nahlaste hlavnímu rozhodčímu.`,
          "error"
        );
      } else {
        showToast("Žádné změny k uložení.", "info");
      }
      return;
    }
    // saveRow nezamítá → totéž co Promise.allSettled (ten starší prohlížeče
    // tabletů nemusí znát); řádky si spinner, „Uloženo“ i chybu nastaví samy
    await Promise.all(pending);
    const notSaved = bulk.notSaved.concat(skippedRejected).sort((a, b) => a - b);
    const savedCount = bulk.saved.length;
    if (notSaved.length === 0) {
      if (mountedRef.current) showToast(`Uloženo ${savedCount} ${resultsWord(savedCount)}.`, "success");
    } else {
      // Důvod je u řádku – jen u řádků, které obrazovka pořád ukazuje; u ostatních
      // (rozhodčí mezitím přešel na jinou rundu / stanoviště) přímo v hlášce
      const atRow = notSaved.length > bulk.elsewhere.length;
      showToast(
        `Uloženo ${savedCount}, neuloženo ${notSaved.length} (${notSaved.map(sn => `#${sn}`).join(", ")})` +
          (atRow ? " – důvod je u řádku." : ".") +
          (bulk.elsewhere.length > 0 ? " " + bulk.elsewhere.join(" ") : ""),
        "error"
      );
    }
  };

  // ── Nový výběr stanoviště / rundy ─────────────────────────
  // scores jsou keyed by startNumber – bez resetu by hodnoty z jiného stanoviště
  // zůstaly v inputech a mohly by přepsat správně uložené výsledky. Rozepsané
  // hodnoty se zahodí a řádky se znovu předvyplní uloženými výsledky.
  const resetRows = () => {
    selectionRef.current += 1;
    setSelectionSeq(selectionRef.current);
    dirtyRef.current = {};
    baselineRef.current = {};
    inFlightRef.current = {};
    rejectedRef.current = {};
    setScores({});
    setErrors({});
    setZoneCounts({});
    setExpandedShooter(null);
  };

  // ── Reset scores při změně stanoviště ────────────────────
  useEffect(() => {
    resetRows();
    setSelectedRoundNum(null);
  // Jen při změně stanoviště (resetRows je nová funkce při každém vykreslení)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station.id]);

  // Automatická rotace: bez vybrané rundy se předvybere ta, která stojí na
  // terči (vybranou rundu rozhodčímu nic nepřepne)
  const suggestedRoundNum = autoRotation ? autoRotation.suggested : null;
  useEffect(() => {
    if (selectedRoundNum !== null || suggestedRoundNum === null) return;
    setSelectedRoundNum(suggestedRoundNum);
    resetRows();
  // resetRows je nová funkce při každém vykreslení
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoundNum, suggestedRoundNum]);

  // ── Předvyplnění stávajících výsledků ─────────────────────
  // Neupravený řádek ukazuje vždy uložený výsledek TOHOTO stanoviště: obnoví se
  // po opravě hlavním rozhodčím a po smazání výsledku se vyprázdní. Upravený
  // řádek zůstane, jak ho rozhodčí napsal (do uložení nebo nového výběru).
  useEffect(() => {
    if (!selectedRound) return;
    const fill: { [startNum: number]: string } = {};
    const fillCounts: { [startNum: number]: { [zone: number]: number } } = {};
    for (const shooter of selectedRound.shooters) {
      const sn = shooter.startNumber;
      if (dirtyRef.current[sn] || inFlightRef.current[sn] !== undefined) continue;
      const stored = storedScore(sn);
      baselineRef.current[sn] = stored;
      fill[sn] = stored === null ? "" : toInput(stored);
      // Po ranách (4b): výběr zón z uložených ran; starší výsledek jen se
      // součtem (nebo bez výsledku) prázdný
      if (perShot) {
        const shots = storedShots(sn);
        fillCounts[sn] = (shots && countsOfShots(shots, zones)) || {};
      }
    }
    if (perShot) {
      setZoneCounts(prev => {
        let changed = false;
        const next = { ...prev };
        for (const key of Object.keys(fillCounts)) {
          const sn = Number(key);
          if (dirtyRef.current[sn] || inFlightRef.current[sn] !== undefined) continue;
          if (sameShots(shotsOfCounts(next[sn] || {}), shotsOfCounts(fillCounts[sn]))) continue;
          next[sn] = fillCounts[sn];
          changed = true;
        }
        return changed ? next : prev;
      });
    }
    setScores(prev => {
      let changed = false;
      const next = { ...prev };
      for (const key of Object.keys(fill)) {
        const sn = Number(key);
        // Mezitím upravený nebo ukládaný řádek nepřepisovat
        if (dirtyRef.current[sn] || inFlightRef.current[sn] !== undefined || (next[sn] ?? "") === fill[sn]) continue;
        next[sn] = fill[sn];
        changed = true;
      }
      return changed ? next : prev;
    });
  // selectedRound, zones, perShot, storedScore a storedShots se odvozují
  // z uvedených závislostí (funkce jsou nové při každém vykreslení)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoundNum, station.id, liveResults, rounds, selectionSeq, target]);

  // ─────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────


  // ── Zone picker helpers ───────────────────────────────────
  const getZoneCounts = (startNum: number): { [zone: number]: number } =>
    zoneCounts[startNum] || {};

  const setZoneCount = (startNum: number, zone: number, delta: number) => {
    dirtyRef.current[startNum] = true; // zadání po ranách = úprava řádku
    delete rejectedRef.current[startNum];
    setZoneCounts(prev => {
      const current = prev[startNum] || {};
      // Rychlé dvojí „+“: počet ran z aktuálního stavu, ne z vykreslení
      if (delta > 0 && Object.values(current).reduce((s, v) => s + v, 0) >= shotsCount) return prev;
      const currentVal = current[zone] || 0;
      const newVal = Math.max(0, currentVal + delta);
      const updated = { ...current, [zone]: newVal };
      // Spočítej celkový skóre a aktualizuj input (bez ran prázdné)
      const totalShots = Object.values(updated).reduce((s, v) => s + v, 0);
      if (totalShots === 0) {
        setScores(sp => ({ ...sp, [startNum]: "" }));
      } else if (totalShots <= shotsCount) {
        const totalScore = Object.entries(updated).reduce(
          (s, [z, cnt]) => s + parseFloat(z) * cnt, 0
        );
        const rounded = Math.round(totalScore * 100) / 100;
        setScores(sp => ({ ...sp, [startNum]: String(rounded).replace(".", ",") }));
      }
      return { ...prev, [startNum]: updated };
    });
  };

  const getTotalZoneShots = (startNum: number): number => {
    const counts = getZoneCounts(startNum);
    return Object.values(counts).reduce((s, v) => s + v, 0);
  };

  return (
    <div className="px-3 pt-3 pb-5" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
      {/* Hlavička terče */}
      <div className="card shadow-sm mb-3">
        <div className="card-body py-2">
          <div className="d-flex align-items-center">
            <span className="fs-4 me-2">🎯</span>
            <div>
              <div className="fw-bold">{station.name}</div>
              <div className="text-muted small">
                {targetName || "Terč"} &nbsp;·&nbsp; {shotsCount} ran
                {target && (
                  <span className="ms-2">
                    Zóny:{" "}
                    {target.zones.map((z, i) => (
                      <span
                        key={i}
                        className={`badge me-1 ${z < 0 ? "bg-danger" : z >= 30 ? "bg-success" : "bg-secondary"}`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        {z}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Výběr rundy */}
      <div className="mb-3">
        <label className="form-label fw-semibold">Vyberte rundu</label>
        {/* Mezery okraji, ne flex gap (Chrome 80) */}
        <div className="lks-gap-wrap-2">
          {rounds.map(r => {
            const doneCount = r.shooters.filter(s =>
              s.active && liveResults[s.startNumber]?.stations?.[station.id] !== undefined
            ).length;
            const activeCount = r.shooters.filter(s => s.active).length;
            const isDone = doneCount === activeCount && activeCount > 0;
            return (
              <button
                key={r.number}
                className={`btn ${
                  selectedRoundNum === r.number
                    ? "btn-success"
                    : isDone
                    ? "btn-outline-secondary"
                    : "btn-outline-primary"
                }`}
                style={{ minWidth: 100, padding: "0.4rem 0.75rem", fontSize: "0.9rem" }}
                onClick={() => {
                  setSelectedRoundNum(r.number);
                  resetRows();
                }}
              >
                Runda {r.number}
                {isDone && <i className="bi bi-check-circle ms-1" />}
                {!isDone && doneCount > 0 && (
                  <span className="badge bg-warning text-dark ms-1">{doneCount}/{activeCount}</span>
                )}
                {suggestedRoundNum === r.number && (
                  <span className="badge bg-info text-dark ms-1" title="Runda podle automatické rotace stojí na terči">teď</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Seznam střelců */}
      {selectedRound && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              Runda {selectedRound.number}
              <span className="text-muted fw-normal ms-2 small">
                ({activeShooters.length} střelců)
              </span>
            </h6>
            <button
              className="btn btn-success btn-sm"
              onClick={handleSaveAll}
            >
              <i className="bi bi-floppy me-1" />Uložit vše
            </button>
          </div>

          {/* Automatická rotace: runda odstřílela – poslat dál (výsledky jde zadat i potom) */}
          {autoRotation && autoRotation.canRelease && !autoRotation.isSentOn(selectedRound.number) && (
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => autoRotation.onRelease(selectedRound.number)}
              >
                <i className="bi bi-send me-1" />Runda {selectedRound.number} odstřílela – poslat dál
              </button>
            </div>
          )}

          <div className="d-flex flex-column lks-judge-vgap">
            {activeShooters.map(shooter => {
              const raw      = scores[shooter.startNumber] ?? "";
              const existing = liveResults[shooter.startNumber]?.stations?.[station.id];
              const stored     = storedScore(shooter.startNumber);
              const isSaving   = saving[shooter.startNumber];
              const saveError  = errors[shooter.startNumber];
              // Trvale „Uloženo“ (H3): pole ukazuje uloženou hodnotu, nic se
              // neukládá ani nezamítlo
              // Zápis čeká ve schránce (bez signálu, i z dřívějšího otevření
              // obrazovky nebo před obnovením aplikace – místní kopie ho už
              // ukazuje, server ho nepotvrdil): „Uloženo“ ne, „Čeká na odeslání“
              const isQueued = queuedRows.has(shooter.startNumber);
              // Po ranách (4b): uložené rány souhlasí s výběrem zón
              const rowShots = shotsOfCounts(getZoneCounts(shooter.startNumber));
              const savedShots = storedShots(shooter.startNumber);
              const shotsMatch = !perShot || (!!savedShots && sameShots(savedShots, rowShots));
              const isSavedRow = showsScore(raw, stored) && shotsMatch && !isSaving && !saveError && !isQueued;
              // Kontrola: po ranách jen rozepsaný řádek (neúplné rány), jinak zadaná hodnota
              const { valid, reason } = perShot
                ? (dirtyRef.current[shooter.startNumber] && rowShots.length > 0
                    ? validateRow(shooter.startNumber, raw)
                    : { valid: true, reason: undefined })
                : raw.trim() !== ""
                ? validateScore(raw)
                : { valid: true, reason: undefined };
              // Uložený výsledek bez rozpisu ran (zadaný součtem před 4b, oprava jen bodů)
              const withoutShots = perShot && stored !== null && !savedShots;
              // Po ranách rozepsané, ještě ne všechny rány (nápověda, ne chyba)
              const shotsIncomplete = perShot && rowShots.length > 0 && rowShots.length < shotsCount;

              return (
                <div
                  key={shooter.startNumber}
                  className={`card shadow-sm ${isSavedRow ? "border-success" : ""}`}
                >
                  <div className="card-body py-2 px-3">
                    {/* Řádek střelce: na úzkém telefonu se ovládání (po ranách,
                        body, uložit) zalomí pod jméno – dřív se pole bodů
                        zmáčklo na pár px a Uložit bylo za okrajem (360 px).
                        Mezery okraji, ne flex gap (Chrome 80). */}
                    <div className="lks-target-row">
                      {/* Startovní číslo */}
                      <span
                        className="badge bg-secondary me-3"
                        style={{ fontSize: "0.9rem", minWidth: 44 }}
                      >
                        #{shooter.startNumber}
                      </span>

                      {/* Jméno */}
                      <div className="flex-grow-1 me-2" style={{ minWidth: "7rem", flexBasis: 0 }}>
                        <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                          {shooter.name}
                        </div>
                        {shooter.sequenceNumber && (
                          <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                            LKS #{shooter.sequenceNumber}
                          </div>
                        )}
                      </div>

                      <div className="lks-target-row__controls">
                      {/* Zone picker tlačítko – nalevo od inputu. Jen otevře
                          a zavře výběr zón, nikdy neukládá (H2) – uloží
                          „OK – Uložit“ nebo tlačítko u řádku */}
                      {perShot && (
                        <button
                          className={`btn btn-sm ${expandedShooter === shooter.startNumber ? "btn-primary" : "btn-outline-primary"}`}
                          style={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}
                          aria-expanded={expandedShooter === shooter.startNumber}
                          onClick={() => {
                            setExpandedShooter(expandedShooter === shooter.startNumber ? null : shooter.startNumber);
                          }}
                        >
                          <i className="bi bi-grid-3x3-gap me-1" />
                          Zadat po ranách
                          {/* Odznak jen u rozepsaného řádku (uložené rány ho nemají – řádek
                              zůstane stejně úzký jako dřív) */}
                          {getTotalZoneShots(shooter.startNumber) > 0 && dirtyRef.current[shooter.startNumber] && (
                            <span className="badge bg-warning text-dark ms-1">
                              {getTotalZoneShots(shooter.startNumber)}/{shotsCount}
                            </span>
                          )}
                        </button>
                      )}

                      {/* Vstup – score (pevná šířka, nezmáčkne se) */}
                      <div style={{ width: 100, flexShrink: 0 }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`form-control form-control-sm text-center font-monospace ${
                            raw.trim() !== "" && !valid && !shotsIncomplete ? "is-invalid" : ""
                          }`}
                          style={{ fontSize: "16px" }}  /* iOS: font-size >= 16px zabraňuje auto-zoomu */
                          placeholder={perShot ? "po ranách" : existing ? String(existing.score).replace(".", ",") : "body"}
                          value={raw}
                          // Fáze 4b: body jsou součet ran – psát nejde, klepnutí otevře výběr zón
                          readOnly={perShot}
                          aria-label={perShot ? `Body – součet ran #${shooter.startNumber}` : undefined}
                          onClick={perShot ? () => setExpandedShooter(shooter.startNumber) : undefined}
                          onChange={e => {
                            if (perShot) return;
                            const value = e.target.value;
                            dirtyRef.current[shooter.startNumber] = true;
                            delete rejectedRef.current[shooter.startNumber];
                            setScores(prev => ({
                              ...prev,
                              [shooter.startNumber]: value,
                            }));
                          }}
                          onKeyDown={e => { if (e.key === "Enter") handleSave(shooter); }}
                        />
                      </div>

                      {/* Uložit */}
                      <button
                        className={`btn btn-sm ${isSavedRow ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => handleSave(shooter)}
                        disabled={isSaving || raw.trim() === ""}
                        style={{ minWidth: 44 }}
                        aria-label={isSavedRow ? "Uloženo" : "Uložit"}
                        title={isSavedRow ? "Uloženo" : "Uložit"}
                      >
                        {isSaving
                          ? <span className="spinner-border spinner-border-sm" />
                          : isSavedRow
                          ? <i className="bi bi-check-lg" />
                          : <i className="bi bi-floppy" />
                        }
                      </button>
                      </div>
                    </div>

                    {/* Validační chyba; po ranách (4b) neúplné rány jen jako nápověda –
                        rozhodčí je teprve zadává */}
                    {(perShot || raw.trim() !== "") && !valid && (
                      <div className={`${shotsIncomplete ? "text-muted" : "text-danger"} mt-1`} style={{ fontSize: "0.75rem" }}>
                        <i className={`bi ${shotsIncomplete ? "bi-grid-3x3-gap" : "bi-exclamation-triangle"} me-1`} />
                        {reason}
                      </div>
                    )}

                    {/* Neuložený výsledek – zápis zamítnutý (docs/live-faze1-navrh.md, 3.6) */}
                    {saveError && (
                      <div className="text-danger mt-1" style={{ fontSize: "0.75rem" }}>
                        <i className="bi bi-exclamation-triangle me-1" />
                        {saveError}
                      </div>
                    )}

                    {/* Uložený výsledek (H3): trvale „Uloženo“, nebo uložená
                        hodnota vedle rozepsané změny */}
                    {isSavedRow ? (
                      <div className="text-success mt-1" style={{ fontSize: "0.75rem" }}>
                        <i className="bi bi-check-circle-fill me-1" />
                        Uloženo
                      </div>
                    ) : isQueued && !isSaving && !saveError && showsScore(raw, stored) ? (
                      <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                        <i className="bi bi-cloud-arrow-up me-1" />
                        Čeká na odeslání – uloží se, až bude signál
                      </div>
                    ) : existing && stored !== null && !isSaving && !saveError ? (
                      <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                        <i className="bi bi-check-circle text-success me-1" />
                        Uloženo: {toInput(stored)} b.
                        {withoutShots
                          ? " · bez rozpisu ran – zadejte po ranách"
                          : raw.trim() !== "" ? " · změna není uložená" : ""}
                      </div>
                    ) : null}



                    {/* Zone picker – rozbalitelný */}
                    {expandedShooter === shooter.startNumber && perShot && (
                      <div className="mt-2 p-2 rounded" style={{ background: "#f8f9fa", border: "1px solid #dee2e6" }}>
                        <div className="lks-gap-wrap-2 mb-1">
                          {zones.map(zone => {
                            const count = getZoneCounts(shooter.startNumber)[zone] || 0;
                            const totalShots = getTotalZoneShots(shooter.startNumber);
                            const canAdd = totalShots < shotsCount;
                            const badgeColor = zone < 0 ? "bg-danger" : zone === 0 ? "bg-secondary" : zone >= 30 ? "bg-success" : "bg-primary";
                            return (
                              <div key={zone} className="d-flex align-items-center"
                                style={{ border: "1px solid #dee2e6", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                                {/* Název zóny NxHODNOTA */}
                                <div className={`badge ${badgeColor} d-flex align-items-center`}
                                  style={{ borderRadius: "7px 0 0 7px", fontSize: "0.82rem", minWidth: 54, justifyContent: "center", padding: "0.35rem 0.5rem" }}>
                                  {count}×{zone}
                                </div>
                                {/* Minus */}
                                <button
                                  className="btn btn-sm py-0 px-2"
                                  style={{ fontSize: "1.3rem", lineHeight: 1.6, borderRadius: 0, borderRight: "1px solid #dee2e6", color: "#dc3545", minWidth: 36 }}
                                  onClick={() => setZoneCount(shooter.startNumber, zone, -1)}
                                  disabled={count === 0}
                                >−</button>
                                {/* Plus */}
                                <button
                                  className="btn btn-sm py-0 px-2"
                                  style={{ fontSize: "1.3rem", lineHeight: 1.6, borderRadius: "0 7px 7px 0", color: "#198754", minWidth: 36 }}
                                  onClick={() => setZoneCount(shooter.startNumber, zone, 1)}
                                  disabled={!canAdd}
                                >+</button>
                              </div>
                            );
                          })}
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="text-muted small">
                            Ran: {getTotalZoneShots(shooter.startNumber)}/{shotsCount}
                            {raw.trim() !== "" && <span className="ms-2 fw-semibold">= {raw} b.</span>}
                          </span>
                          <button
                            className="btn btn-success btn-sm"
                            disabled={getTotalZoneShots(shooter.startNumber) !== shotsCount || saving[shooter.startNumber]}
                            onClick={() => {
                              setExpandedShooter(null);
                              handleSave(shooter);
                            }}
                          >
                            <i className="bi bi-check-lg me-1" />OK – Uložit
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!selectedRound && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-arrow-up-circle fs-2 d-block mb-2" />
          Vyberte rundu výše
        </div>
      )}
    </div>
  );
};

export default JudgeTarget;
